import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import type { Tenant } from './models';

interface AppState {
  selectedTenant: Tenant | null;
  currentNavIndex: number;
}

interface AppContextValue extends AppState {
  hasSelectedTenant: boolean;
  selectTenant: (tenant: Tenant) => void;
  clearTenant: () => void;
  setNavIndex: (index: number) => void;
}

const AppContext = createContext<AppContextValue | null>(null);

export const PREFS_SELECTED_TENANT = 'saved_selected_tenant';

function readSavedTenant(): Tenant | null {
  try {
    const raw = localStorage.getItem(PREFS_SELECTED_TENANT);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed === 'object' &&
      typeof parsed.tenantId === 'string' &&
      parsed.tenantId.trim() !== '' &&
      typeof parsed.name === 'string'
    ) {
      return parsed as Tenant;
    }
  } catch {
    // localStorage may be unavailable or raw JSON invalid
  }
  return null;
}

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AppState>(() => ({
    selectedTenant: readSavedTenant(),
    currentNavIndex: 0,
  }));

  const selectTenant = useCallback((tenant: Tenant) => {
    setState((s) => ({ ...s, selectedTenant: tenant }));
    try {
      localStorage.setItem(PREFS_SELECTED_TENANT, JSON.stringify(tenant));
    } catch {
      // ignore
    }
  }, []);

  const clearTenant = useCallback(() => {
    setState((s) => ({ ...s, selectedTenant: null }));
    try {
      localStorage.removeItem(PREFS_SELECTED_TENANT);
    } catch {
      // ignore
    }
  }, []);

  const setNavIndex = useCallback((index: number) => {
    setState((s) => ({ ...s, currentNavIndex: index }));
  }, []);

  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === PREFS_SELECTED_TENANT) {
        setState((s) => ({ ...s, selectedTenant: readSavedTenant() }));
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  return (
    <AppContext.Provider
      value={{
        ...state,
        hasSelectedTenant: state.selectedTenant !== null,
        selectTenant,
        clearTenant,
        setNavIndex,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useAppState() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useAppState must be used within AppProvider');
  return ctx;
}
